import { useEffect, useRef, useState } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import useProfile from '@/hooks/useProfileV2';
import candidatesServices, { CandidateResponse, CandidateToSubmit } from '@/services/candidates';
import staffMemberService, { CreateStaffMemberByPersonIdProps } from '@/services/staffMember';
import { PersonType } from '@/types/api';
import { StaffMember } from '@/types/api/staffMember';
import NewStaffMemberFormModalComponent, {
  NewStaffMemberFormModalComponentProps,
} from './NewStaffMemberFormModal.component';
import CreateStaffMemberProfileModal from '../CreateStaffMemberProfileModal';

type NewStaffMemberFormModalProps = Pick<
  NewStaffMemberFormModalComponentProps,
  'isOpen' | 'onClose'
> & {
  onCreateStaffMember?: (staffMember: StaffMember) => void;
};

const NewStaffMemberFormModal = ({
  isOpen,
  onClose,
  onCreateStaffMember,
}: NewStaffMemberFormModalProps): JSX.Element => {
  const elementRef = useRef<HTMLDivElement>(null);
  const toast = useToastContext();
  const { profile } = useProfile();
  const [isCreateStaffMemberModalOpen, setIsCreateStaffMemberModalOpen] = useState<boolean>(false);
  const [createStaffMemberProfile, setCreateStaffMemberProfile] = useState<PersonType>();
  const [createStaffMemberMessage, setCreateStaffMemberMessage] = useState<string>('');

  useEffect(() => {
    const onPageClick = (event: any): void => {
      if (!isOpen) return;

      const eventPath = event.composedPath() ?? [];

      const doesElementContainId = (element: any): boolean => {
        return element === elementRef.current;
      };

      const isClickingInsideForm = eventPath.some(doesElementContainId);

      if (!isCreateStaffMemberModalOpen && !isClickingInsideForm) {
        onClose?.();
      }
    };

    window.addEventListener('mousedown', onPageClick);

    return () => {
      window.removeEventListener('mousedown', onPageClick);
    };
  }, [elementRef, onClose, isOpen, isCreateStaffMemberModalOpen]);

  const createCandidateMutation = useApiSubmit<CandidateResponse, CandidateToSubmit>(
    candidatesServices.createCandidate
  );

  const createStaffMemberMutation = useApiSubmit<StaffMember, CreateStaffMemberByPersonIdProps>(
    staffMemberService.createStaffMemberByPersonId
  );

  const createStaffMemberByPerson = async (person: PersonType): Promise<void> => {
    try {
      await staffMemberService.getStaffMemberByPersonId({
        personId: person.id,
      });
    } catch {
      setCreateStaffMemberProfile(person);
      setIsCreateStaffMemberModalOpen(true);
      setCreateStaffMemberMessage(
        `We've identified a profile named ${person.firstName}${
          person.lastName ? ` ${person.lastName}` : ''
        } in the database associated with this personal email. Would you like to create a staff member profile for this person and import their information?`
      );
    }
  };

  const createStaffMemberByEmail = async (email: string): Promise<void> => {
    try {
      const response = await candidatesServices.getFilteredCandidates({
        take: 1,
        page: 1,
        column: 'firstName',
        order: 'ASC',
        filters: JSON.stringify([
          {
            field: 'email',
            condition: 'is',
            value: email,
          },
        ]),
      });

      const personFound: PersonType | undefined = response?.data.data[0];

      if (!personFound) {
        throw new Error();
      }

      createStaffMemberByPerson(personFound);
    } catch (error) {
      toast.setMessage(
        'Person already exists but the server failed trying to find them. Please contact support.'
      );
      toast.setSeverity('error');
      toast.showToast();
    }
  };

  const handleSubmit: NewStaffMemberFormModalComponentProps['onSubmit'] = async (
    data
  ): Promise<void> => {
    try {
      toast.setMessage('Creating new person...');
      toast.setSeverity('info');
      toast.showToast();

      const { candidate: newPerson } = await createCandidateMutation.mutateAsync({
        ...data,
        ownerId: profile.id,
      });

      toast.setMessage('Creating staff member...');

      const staffMember = await createStaffMemberMutation.mutateAsync({
        personId: newPerson.id,
        hiringDate: new Date(),
      });

      toast.setMessage('The staff member has been created.');
      toast.setSeverity('success');
      toast.showToast();
      onCreateStaffMember?.(staffMember);
    } catch (error: any) {
      if (error?.response?.data?.message === 'Person already exists' && data.email) {
        createStaffMemberByEmail(data.email);
      }

      toast.setMessage(error?.response?.data?.message || 'There was an error saving the data.');
      toast.setSeverity('error');
      toast.showToast();
    }
  };

  const handleCreateStaffMember = (staffMember: StaffMember): void => {
    toast.setMessage('The staff member has been created.');
    toast.setSeverity('success');
    toast.showToast();
    onCreateStaffMember?.(staffMember);
  };

  return (
    <>
      <NewStaffMemberFormModalComponent
        isOpen={isOpen}
        onClose={onClose}
        ref={elementRef}
        onSubmit={handleSubmit}
        isLoading={createCandidateMutation.isLoading || createStaffMemberMutation.isLoading}
      />
      {createStaffMemberProfile && (
        <CreateStaffMemberProfileModal
          isOpen={isCreateStaffMemberModalOpen}
          onClose={() => setIsCreateStaffMemberModalOpen(false)}
          person={createStaffMemberProfile}
          onCreateStaffMember={handleCreateStaffMember}
          message={createStaffMemberMessage}
        />
      )}
    </>
  );
};

export default NewStaffMemberFormModal;
